import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAboutSite } from '../context/AboutSiteContext.jsx'
import { lenis } from '../lib/lenis.js'

function Section({ eyebrow, title, children }) {
  return (
    <section className="border-t border-soft-rule pt-6 pb-1">
      <p className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint mb-2">
        {eyebrow}
      </p>
      <h2 className="text-[20px] font-medium tracking-[-0.4px] text-soft-ink mb-3">
        {title}
      </h2>
      <div className="text-[14.5px] leading-[1.7] text-soft-dim space-y-3">{children}</div>
    </section>
  )
}

function Pill({ children }) {
  return (
    <span className="inline-block px-2 py-1 bg-soft-surface border border-soft-rule text-[11.5px] font-mono text-soft-dim">
      {children}
    </span>
  )
}

const STACK = [
  ['Django 6', 'Django REST Framework', 'SQLite / PostgreSQL', 'Python 3.12'],
  ['React 19', 'Vite', 'Tailwind CSS', 'framer-motion'],
  ['Neo4j', 'sentence-transformers', 'LangGraph', 'Ollama Cloud', 'OpenRouter', 'NVIDIA NIM'],
  ['Docker', 'EC2 + EBS', 'S3 + CloudFront', 'GitHub Actions', 'nginx + gunicorn'],
]

function AboutSiteOverlay() {
  const { isOpen, closeAbout } = useAboutSite()

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      lenis.stop()
      return () => {
        document.body.style.overflow = ''
        lenis.start()
      }
    }
  }, [isOpen])

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeAbout()
      }
    }
    if (isOpen) {
      window.addEventListener('keydown', handleKey)
      return () => window.removeEventListener('keydown', handleKey)
    }
  }, [isOpen, closeAbout])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 sm:p-6"
          onClick={closeAbout}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="About this site"
            data-lenis-prevent="true"
            className="relative w-full max-w-[720px] max-h-[85vh] overflow-y-auto bg-soft-surface border border-soft-rule shadow-[0_24px_64px_rgba(0,0,0,0.18)]"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-soft-surface/95 backdrop-blur border-b border-soft-rule px-6 py-4 flex justify-between items-center">
              <p className="font-mono text-[11px] uppercase tracking-[0.8px] text-soft-faint">
                About this site
              </p>
              <button
                onClick={closeAbout}
                className="text-soft-dim hover:text-soft-ink cursor-pointer text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="px-6 sm:px-10 py-8 pb-12">
              <h1 className="text-[28px] sm:text-[34px] font-normal leading-[1.08] tracking-[-1.2px] mb-4">
                How this thing works.
              </h1>
              <p className="text-[16px] leading-[1.6] text-soft-dim mb-6">
                This is my portfolio, and it's also a bit of an experiment. There's
                an agent running on here that's trained on me, on what I've actually
                done and how I'd probably answer you. So here's how the whole thing
                hangs together. No jargon, I promise. Well, minimal jargon.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {STACK.map((group, gi) => (
                  <div key={gi} className="flex flex-wrap gap-2">
                    {group.map((tech) => (
                      <Pill key={tech}>{tech}</Pill>
                    ))}
                  </div>
                ))}
              </div>

              <Section eyebrow="Architecture" title="React + Django">
                <p>
                  Django 6 runs the backend. It serves the content you're reading
                  and answers every request the agent makes. I manage projects,
                  experience, skills, and posts through Django's admin, and the
                  data lands in a plain database. The frontend's React, styled
                  with Tailwind.
                </p>
              </Section>

              <Section eyebrow="The agent" title="The agent that answers for me">
                <p>
                  When you ask a question, it gets turned into a vector locally
                  with a small embedding model, then matched against my
                  knowledge graph in Neo4j. It pulls context from a few hundred
                  Q&A pairs I wrote myself, plus my projects, experience,
                  education, skills, and writing. Then a language model writes
                  the reply in my voice. Not just that, it can detect the intent,
                  route you through the site, take your contact details (if you intend to contact me).
                </p>
                <p>
                  The model itself is swappable. I wrote a thin wrapper so I can
                  point the agent at Ollama Cloud, NVIDIA, OpenRouter, or a
                  local model without touching the rest of the code. Right now
                  it runs on Ollama Cloud's gpt-oss:120b.
                </p>
              </Section>

              <Section eyebrow="Learning loop" title="It gets corrected">
                <p>
                  You can vote on every answer and leave a correction. Those go
                  back into the system as preference pairs for future fine
                  tuning. Consent is recorded up front, and visitor data stays
                  in the app database, never in the graph.
                </p>
              </Section>

              <Section eyebrow="Content" title="CMS">
                <p>
                  Posts, projects etc., are managed using a custom content management system which is built for this site, which i can access through the admin panel.
                </p>
              </Section>

              <Section eyebrow="Graph" title="The memory">
                <p>
                  Only content goes into Neo4j: bio, experience, education,
                  projects, skills, blog posts, and QA. Chats, sessions, and
                  comments stay in the normal database. That keeps the graph
                  clean and the sensitive stuff out of it.
                </p>
              </Section>

              <Section eyebrow="Infrastructure" title="How it ships">
                <p>
                  Deployed using a Docker image, nginx in front of gunicorn, running on a
                  small EC2 box with an EBS volume for data. Assets sit behind
                  CloudFront, and every push to main runs tests, builds the
                  image, and deploys it through GitHub Actions. The Graph database is hosted in a free-tier Neo4j AuraDB.
                </p>
              </Section>
              <Section eyebrow="Disclaimer" title={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                <path d="M12 9v4"/>
                <path d="M12 17h.01"/>
              </svg>
              }>
                <p>
                  In-case if it breaks, the agent spits out rubbish despite the guard rails, and if you're nice enough to notice it and get back to me,
                  please shoot a contact message, through the site (or) an email (jc.mugunth@gmail.com). I'd really love to get to the bottom of this, Thanking you in advance!.
                </p>
              </Section>

              <div className="mt-10 pt-6 border-t border-soft-rule flex justify-between items-center">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint">
                  Built by Mugunth J. Chandirasekaran
                </span>
                <button
                  onClick={closeAbout}
                  className="text-sm text-soft-dim hover:text-soft-ink transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default AboutSiteOverlay
